import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { useQuery } from '@tanstack/react-query'
import { type ComponentProps, type FC, useState } from 'react'
import { Col, Form } from 'react-bootstrap'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { icdCodesQuery } from '../../api/index.js'
import { fontFamily } from '../grid.js'

const autocompleteStyle = (hasValue: boolean) => ({
	padding: '0.4rem 0',
	backgroundColor: hasValue ? 'rgba(230, 243, 254, 1)' : '#f0f3f7',
	color: hasValue ? 'rgba(0, 58, 120, 1)' : '#333333',
	borderBottom: '1px solid #333333',
	borderRadius: '4px 4px 0 0',
	font: `400 13px/20px ${fontFamily}, "Segoe UI", sans-serif`,
	'&:focus, &:hover': {
		backgroundColor: hasValue ? 'rgba(230, 243, 254, 1)' : '#f0f3f7',
		borderBottom: '1px solid #333333',
	},
	'.MuiFormLabel-root': {
		color: 'rgba(0, 58, 120, 1)',
		font: '12px Borna, "Segoe UI", sans-serif !important',
		top: '6px',
	},
})

const textFieldStyle = {
	border: 0,
	'.MuiOutlinedInput-notchedOutline': { border: 0 },
	input: {
		border: 0,
		outline: 0,
		font: `400 13px/20px ${fontFamily}, "Segoe UI", sans-serif`,
	},
}

interface IcdCodesSelectProps {
	name: string
	label: string
	formGroupProps?: ComponentProps<typeof Form.Group>
	required?: boolean
	disabled?: boolean
	customErrorMessage?: { [key: string]: string }
	additionalClassnames?: string
}

// TODO: virtualize listbox (13k+ codes) - https://mui.com/material-ui/react-autocomplete/#virtualization
export const IcdCodesSelect: FC<IcdCodesSelectProps> = ({
	name,
	label,
	formGroupProps,
	required = false,
	disabled,
	customErrorMessage,
	additionalClassnames,
}) => {
	const { t } = useTranslation()
	const { data: allCodes } = useQuery(icdCodesQuery)
	const { control } = useFormContext()
	const [options, setOptions] = useState<string[]>([])

	const filterCodes = (input: string) => {
		if (!input || !allCodes) return []
		const upper = input.toUpperCase()
		return (allCodes as { value: string }[])
			.filter(({ value }) => value.startsWith(upper))
			.slice(0, 200)
			.map(({ value }) => value)
	}

	return (
		<Controller
			name={name}
			control={control}
			shouldUnregister
			defaultValue={null}
			rules={{ required }}
			render={({ field: { value, onChange, disabled: globalDisabled }, fieldState: { error } }) => {
				const errorType = error?.type
				const helperText = () => {
					if (required && errorType === 'required') return 'The field is required'
					return customErrorMessage?.[errorType!] ?? error?.message ?? ''
				}

				return (
					<Form.Group
						as={Col}
						xs={12}
						className={`d-flex flex-column mb-2 ${additionalClassnames ?? ''}`}
						{...formGroupProps}
					>
						<Autocomplete
							options={options}
							value={value ?? null}
							onChange={(_, selected) => onChange(selected ?? null)}
							onOpen={() => {
								if (value) setOptions(filterCodes(value.charAt(0)))
							}}
							onInputChange={(_, input, reason) => {
								if (reason === 'clear') {
									setOptions([])
								} else if (reason === 'reset' || reason === 'selectOption') {
									if (input) setOptions(filterCodes(input.charAt(0)))
								} else {
									setOptions(filterCodes(input))
								}
							}}
							disabled={disabled || globalDisabled}
							filterOptions={(x) => x}
							noOptionsText={allCodes ? t('TypeToSearch') : t('Loading')}
							sx={autocompleteStyle(!!value)}
							slotProps={{
								listbox: {
									sx: {
										fontSize: '13px',
										fontFamily: `${fontFamily}, "Segoe UI", sans-serif`,
									},
								},
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									label={label}
									error={!!error}
									required={required}
									size="small"
									slotProps={{
										...params.slotProps,
										inputLabel: {
											...params.slotProps?.inputLabel,
											shrink: true,
										},
									}}
									sx={textFieldStyle}
								/>
							)}
						/>
						{error && (
							<Form.Control.Feedback type="invalid" className="d-block mb-1">
								{helperText()}.
							</Form.Control.Feedback>
						)}
					</Form.Group>
				)
			}}
		/>
	)
}
